import { REPORT_FORMAT } from '../../services/reportService';
import {
    COMPANY_REPORT_ROLES,
    TRANSPORT_COMPANY_REPORT_ROLES,
} from '../../constants/roles';
import { getReportDeliveryStateOptions } from '../Deliveries/statusConfig';
import { getDefaultReportPeriod, resolveFiltersForRoles } from './reportFilterUtils';

/** Período de criação: mesmo intervalo em todos os relatórios de entrega. */
const START_DATE_FILTER = {
    name: 'startDate',
    label: 'Data inicial',
    type: 'date',
    required: true,
};

const END_DATE_FILTER = {
    name: 'endDate',
    label: 'Data final',
    type: 'date',
    required: true,
    hint: 'O limite de dias entre a data inicial e a data final vem dos parâmetros do sistema.',
};

const FORMAT_FILTER = {
    name: 'format',
    label: 'Formato',
    type: 'radio',
    required: true,
    defaultValue: REPORT_FORMAT.PDF,
    options: [
        { value: REPORT_FORMAT.PDF, label: 'PDF' },
        { value: REPORT_FORMAT.XLSX, label: 'Planilha (XLSX)' },
    ],
};

const EXCLUDE_INVOICED_FILTER = {
    name: 'excludeInvoiced',
    label: 'Excluir entregas já faturadas',
    type: 'checkbox',
    defaultValue: false,
    hint: 'Remove entregas cuja cobrança está em alguma fatura, inclusive rascunho; canceladas voltam a aparecer.',
};

const STATE_FILTER_OPTIONS = getReportDeliveryStateOptions();

/**
 * Relatórios disponíveis na tela.
 * Lista chumbada de propósito: o modal lê só `filters` e não depende desta estrutura.
 * Depois podemos trocar por GET /api/relatorios/tipos sem mudar o modal.
 */
export const REPORTS = [
    {
        id: 'delivery-listing',
        type: 'DELIVERY_LISTING',
        title: 'Entregas',
        description:
            'Lista entregas por período de criação, com filtros opcionais de cliente, motorista e situação.',
        roles: TRANSPORT_COMPANY_REPORT_ROLES,
        filters: [
            START_DATE_FILTER,
            END_DATE_FILTER,
            {
                name: 'customerId',
                label: 'Cliente',
                type: 'customer',
                displayNameKey: 'customerName',
                hint: 'Quando omitido, o relatório considera todos os clientes.',
            },
            {
                name: 'driverId',
                label: 'Motorista',
                type: 'driver',
                displayNameKey: 'driverName',
            },
            {
                name: 'states',
                label: 'Situação',
                type: 'multiselect',
                defaultValue: [],
                emptyAsAllOptions: true,
                options: STATE_FILTER_OPTIONS,
                hint: 'Sem seleção (Todos), inclui todas as situações de entrega.',
            },
            EXCLUDE_INVOICED_FILTER,
            FORMAT_FILTER,
        ],
    },
    {
        id: 'delivery-listing-by-company',
        type: 'DELIVERY_LISTING_BY_COMPANY',
        title: 'Entregas por empresa',
        description:
            'Lista as entregas de um cliente por período de criação, com filtros opcionais de centro de custo e situação.',
        roles: COMPANY_REPORT_ROLES,
        filters: [
            START_DATE_FILTER,
            END_DATE_FILTER,
            {
                name: 'customerId',
                label: 'Cliente',
                type: 'customer',
                displayNameKey: 'customerName',
                /** O cliente emite para a própria empresa: o backend usa o customer do token. */
                requiredForRoles: TRANSPORT_COMPANY_REPORT_ROLES,
                hiddenForRoles: ['ROLE_LEGAL_PERSON'],
                hint: 'O relatório é emitido para um único cliente.',
            },
            {
                name: 'costCenterId',
                label: 'Centro de custo',
                type: 'costCenter',
                displayNameKey: 'costCenterName',
                dependsOn: 'customerId',
                hint: 'Quando omitido, o relatório considera todos os centros de custo do cliente.',
            },
            {
                name: 'states',
                label: 'Situação',
                type: 'multiselect',
                defaultValue: [],
                emptyAsAllOptions: true,
                options: STATE_FILTER_OPTIONS,
                hint: 'Sem seleção (Todos), inclui todas as situações de entrega.',
            },
            EXCLUDE_INVOICED_FILTER,
            FORMAT_FILTER,
        ],
    },
];

/**
 * Relatórios visíveis para o usuário, com os filtros já resolvidos para as roles dele.
 * A data inicial pré-selecionada cabe no limite consultado (`maxPeriodDays`).
 * @param {string[]} userRoles
 * @param {number} [maxPeriodDays]
 */
export function getReportsForRoles(userRoles = [], maxPeriodDays) {
    const period = getDefaultReportPeriod(maxPeriodDays);
    return REPORTS.filter((report) =>
        report.roles.some((role) => userRoles.includes(role))
    ).map((report) => ({
        ...report,
        filters: resolveFiltersForRoles(report.filters, userRoles).map((filter) => {
            if (filter.name === 'startDate') {
                return { ...filter, defaultValue: period.startDate };
            }
            if (filter.name === 'endDate') {
                return { ...filter, defaultValue: period.endDate };
            }
            return filter;
        }),
    }));
}
